import type { Messages } from "@/i18n/locales/en";
import { legalMessagesNb } from "@/i18n/locales/legalNbData";
import { pagesMessagesNb } from "@/i18n/locales/pagesNbData";

export const nbMessages = {
  meta: {
    description:
      "WasOK — skann produkter, lagre vurderinger og notater, husk hva som var verdt å kjøpe.",
    title: "WasOK — Husk hva som var verdt å kjøpe",
  },
  header: {
    navFeatures: "Funksjoner",
    navHowItWorks: "Slik fungerer det",
    navPrivacy: "Personvern",
    navUseCases: "Bruksområder",
    download: "Last ned",
    openMenu: "Åpne meny",
    closeMenu: "Lukk meny",
    languageAria: "Språk",
  },
  hero: {
    badge: "📱 Nå tilgjengelig på Android",
    title: "Husk hva som var verdt å kjøpe.",
    subtitle:
      "Skann produkter, lagre vurderinger og notater — og husk umiddelbart om et produkt faktisk var bra.",
    googlePlay: "Last ned på Google Play",
    watchDemo: "Se demo",
    privacyLine: "Alle data blir på enheten din.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Skann for å huske",
    snackCard: "Deilig!",
    coffeeCard: "For bitter",
    scrollHint: "Rull for å utforske",
  },
  problems: {
    heading: "Kjenner du deg igjen?",
    subheading:
      "Vi har alle vært der — foran hyllen og prøvd å huske om vi likte det forrige gang.",
    notedAgo: "✓ Du noterte dette for 3 måneder siden",
    items: [
      {
        question: "Hvilken sjokolade likte jeg egentlig?",
        note: "Perfekt søthet",
      },
      {
        question: "Hvorfor sluttet jeg å kjøpe denne sjampoen?",
        note: "Tørket ut håret",
      },
      {
        question: "Var denne kaffen god eller skuffende?",
        note: "For bitter",
      },
    ],
  },
  howItWorks: {
    heading: "Slik fungerer det",
    subheading:
      "Fire enkle steg for å aldri glemme produktmeningene dine igjen.",
    stepPrefix: "Steg",
    steps: [
      {
        title: "Skann en hvilken som helst produktstrekkode",
        description:
          "Rask og enkel skanning med kameraet. Fungerer med alle strekkoder.",
      },
      {
        title: "Lagre vurderinger, kommentarer og bilder",
        description:
          "Vurder produkter, legg til notater, legg ved bilder og loggfør priser og steder.",
      },
      {
        title: "Husk opplevelsen din umiddelbart",
        description:
          "Skann det samme produktet senere og se din tidligere vurdering og notater.",
      },
      {
        title: "Bygg smarte handlelister",
        description:
          "Opprett handlelister fra produkter du allerede stoler på og liker.",
      },
    ],
  },
  privacy: {
    heading: "Handlehistorikken din forblir din.",
    body: "WasOK hjelper deg å huske produkter du har prøvd, samtidig som vurderinger, notater, bilder og handlelister lagres lokalt på enheten din.",
    trustBanner:
      "💚 Bygget rundt handlehistorikken din — ikke rundt innsamling av dataene dine.",
    bullets: [
      "Ingen konto nødvendig",
      "Ingen skysynkronisering nødvendig",
      "Lokal lagring først",
      "Sikkerhetskopier under din kontroll",
      "Valgfri strekkodesøk for produktinformasjon.",
      "Annonsefinansiert med Google AdMob",
    ],
  },
  features: {
    heading: "Alt du trenger",
    subheading:
      "Kraftige funksjoner laget for å gjøre produktsporing enkelt og privat.",
    items: [
      {
        title: "Strekkodeskanner",
        description: "Rask og nøyaktig strekkodeskanning for alle produkter.",
      },
      {
        title: "Produktvurderinger",
        description: "Vurder produkter fra 1–5 stjerner for å huske kvaliteten.",
      },
      {
        title: "Produktbilder",
        description: "Legg ved bilder for å huske emballasje eller detaljer.",
      },
      {
        title: "Kategorier",
        description: "Organiser produkter i egne kategorier.",
      },
      {
        title: "Butikksporing",
        description: "Husk hvor du kjøpte hvert produkt.",
      },
      {
        title: "Prislogging",
        description: "Spor priser for å finne de beste tilbudene.",
      },
      {
        title: "Handlelister",
        description: "Bygg lister fra favorittproduktene dine.",
      },
      {
        title: "Offline-tilgang",
        description: "Fungerer perfekt uten internettforbindelse.",
      },
      {
        title: "Lokal lagring",
        description: "Alle dataene dine lagres sikkert på enheten din.",
      },
    ],
  },
  useCases: {
    heading: "Perfekt for hver hylle",
    subheading:
      "Enten du handler mat, skjønnhet eller husholdningsvarer — WasOK hjelper deg å huske.",
    cards: [
      {
        emoji: "🍫",
        title: "Snacks & godteri",
        description: "Husk hvilke godbiter som faktisk var gode.",
      },
      {
        emoji: "☕",
        title: "Kaffe & drikke",
        description: "Spor kaffer som er verdt å kjøpe igjen.",
      },
      {
        emoji: "💄",
        title: "Kosmetikk & hudpleie",
        description: "Behold notater om hudreaksjoner.",
      },
      {
        emoji: "🧹",
        title: "Husholdningsvarer",
        description: "Unngå å kjøpe produkter som mislyktes igjen.",
      },
    ],
  },
  screenshots: {
    heading: "Vakkert & intuitivt",
    subheading:
      "Et gjennomtenkt grensesnitt som gjør produktsporing til en glede.",
    items: [
      { title: "Skanner" },
      { title: "Produktdetalj" },
      { title: "Vurderingsregistrering" },
      { title: "Søk & historikk" },
      { title: "Handleliste" },
    ],
  },
  cta: {
    heading: "Handleminne for det virkelige livet.",
    body: "Slutt å glemme hvilke produkter som var verdt å kjøpe. Begynn å handle smartere i dag.",
    googlePlay: "Last ned på Google Play",
    scanQr: "Skann for å laste ned",
    tagline: "Privat · Offline først · Enkelt",
  },
  footer: {
    tagline: "Husk hva som var verdt å kjøpe.",
    linksHeading: "Lenker",
    privacyPolicy: "Personvernerklæring",
    terms: "Vilkår for bruk",
    contact: "Kontakt",
    linkFaq: "FAQ",
    linkLicenses: "Åpen kildekode-lisenser",
    linkAdvertising: "Annonsering & inntektsgenerering",
    downloadHeading: "Last ned",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Alle rettigheter forbeholdt. Laget med 💚 for smartere shopping.",
  },
  legal: legalMessagesNb,
  faq: pagesMessagesNb.faq,
  support: pagesMessagesNb.support,
  advertising: pagesMessagesNb.advertising,
} satisfies Messages;
